import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, Container, Grid, Slide, useMediaQuery, useTheme } from '@mui/material';

import {
  useDeleteRuleMutation,
  useGetRuleQuery,
  useGetRulesQuery,
  useSearchRulesQuery,
} from './store/api';
import {
  DeleteRuleDialog,
  RuleDetailPanel,
  RuleFormPanel,
  RulesHeader,
  RulesList,
} from './components';
import type {
  ComplianceRuleDocument,
  LearningRuleDocument,
  ProviderRuleResponse,
  RuleScope,
  RuleType,
} from './types';

/**
 * Rules Management Page
 * Allows providers to view and manage compliance and learning rules
 * Features a split-pane layout for browsing and details
 */
export const RulesManagementPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for filters and pagination
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<RuleScope>('both');
  const [ruleType, setRuleType] = useState<RuleType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Selection state for detail panel - store ID and fetch via query for auto-refresh on invalidation
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);

  // Panel mode: 'view' | 'create' | 'edit'
  const [panelMode, setPanelMode] = useState<'view' | 'create' | 'edit'>('view');

  // Fetch selected rule data via query - auto-refreshes when Rule tag is invalidated
  const { data: selectedRule, isFetching: isSelectedRuleFetching } = useGetRuleQuery(
    selectedRuleId!,
    { skip: !selectedRuleId },
  );

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<ProviderRuleResponse | null>(null);

  // Delete mutation
  const [deleteRule, { isLoading: isDeleting }] = useDeleteRuleMutation();

  // Debounce search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset to page 1 on search change
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Determine if we are searching or listing
  const isSearching = debouncedSearchQuery.length > 0;

  // Standard List Query
  const {
    data: listData,
    isLoading: isListLoading,
    isFetching: isListFetching,
    refetch: refetchRulesList,
  } = useGetRulesQuery(
    {
      page,
      per_page: 20,
      scope,
      rule_type: ruleType === 'all' ? undefined : ruleType,
    },
    { skip: isSearching }, // Skip list query when searching
  );

  // Search Query (GET - automatically refetches when params change or cache is invalidated)
  const {
    data: searchData,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
  } = useSearchRulesQuery(
    {
      query: debouncedSearchQuery,
      rule_type: ruleType === 'all' ? undefined : ruleType,
      top_k: 20,
    },
    { skip: !isSearching }, // Skip when not searching
  );

  // Handlers
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleScopeChange = useCallback((newScope: RuleScope) => {
    setScope(newScope);
    setPage(1);
  }, []);

  const handleRuleTypeChange = useCallback((newType: RuleType | 'all') => {
    setRuleType(newType);
    setPage(1);
  }, []);

  const handleAddRule = useCallback(() => {
    setSelectedRuleId(null);
    setPanelMode('create');
  }, []);

  const handleRuleClick = useCallback((rule: ProviderRuleResponse) => {
    setSelectedRuleId(rule.rule_id);
    setPanelMode('view');
  }, []);

  const handlePanelClose = useCallback(() => {
    setSelectedRuleId(null);
    setPanelMode('view');
  }, []);

  const handleEditRule = useCallback(() => {
    if (selectedRuleId) {
      setPanelMode('edit');
    }
  }, [selectedRuleId]);

  const handleDeleteRule = useCallback(() => {
    if (selectedRule) {
      setRuleToDelete(selectedRule);
      setDeleteDialogOpen(true);
    }
  }, [selectedRule]);

  const handleConfirmDelete = useCallback(async () => {
    if (!ruleToDelete) return;

    try {
      await deleteRule({
        ruleId: ruleToDelete.rule_id,
        ruleType: ruleToDelete.rule_type,
      }).unwrap();
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
      setSelectedRuleId(null);
      setPanelMode('view');
      // Cache invalidation in the mutation will trigger automatic refetch of list/search queries
    } catch (error) {
      console.error('Failed to delete rule:', error);
      // Error handling could be improved with a snackbar notification
    }
  }, [ruleToDelete, deleteRule]);

  const handleDeleteDialogClose = useCallback(() => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  }, [isDeleting]);

  const handleFormSuccess = useCallback(() => {
    // After successful create/edit, switch back to view mode
    // Cache invalidation in the mutation will trigger automatic refetch of list/search/individual rule queries
    if (panelMode === 'create') {
      setSelectedRuleId(null);
      setPanelMode('view');
    } else if (panelMode === 'edit' && selectedRuleId) {
      // Stay in view mode - selectedRule will auto-refresh via query invalidation
      setPanelMode('view');
    }
  }, [panelMode, selectedRuleId]);

  // Derived state for display
  const isLoading = isSearching
    ? isSearchLoading || isSearchFetching
    : isListLoading || isListFetching;

  const rules = isSearching
    ? searchData?.results
        .filter(r => r.record != null && r.record.rule != null)
        .map(r => ({
          rule: r.record!.rule as ComplianceRuleDocument | LearningRuleDocument,
          rule_id: r.metadata.rule_id,
          rule_type: r.metadata.rule_type,
          is_global: r.record!.is_global || false,
          is_disabled: r.metadata.is_disabled || false,
          organization_id: r.metadata.organization_id,
        })) || []
    : listData?.items || [];

  const totalPages = isSearching
    ? 1 // Search doesn't support pagination in this UI yet
    : listData?.total_pages || 1;

  return (
    <Container
      id="rules-workspace"
      sx={{ py: 4, height: '100%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}
     maxWidth={false}>
      <RulesHeader
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        scope={scope}
        onScopeChange={handleScopeChange}
        ruleType={ruleType}
        onRuleTypeChange={handleRuleTypeChange}
        onAddRule={handleAddRule}
      />

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        {/* List Pane */}
        <Grid
          id="rules-list"
          size={{ xs: 12, md: selectedRuleId || panelMode === 'create' || panelMode === 'edit' ? 5 : 12, lg: selectedRuleId || panelMode === 'create' || panelMode === 'edit' ? 4 : 12 }}
          sx={{
            height: '100%',
            minHeight: 0,
            minWidth: 0,
            overflowY: 'auto',
            transition: 'all 0.3s ease',
            display:
              isMobile && (selectedRuleId || panelMode === 'create' || panelMode === 'edit')
                ? 'none'
                : 'block',
          }}
        >
          <RulesList
            rules={rules}
            isLoading={isLoading}
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onRuleClick={handleRuleClick}
          />
        </Grid>

        {/* Detail/Form Pane */}
        {(selectedRuleId || panelMode === 'create' || panelMode === 'edit') && (
          <Grid size={{ xs: 12, md: 7, lg: 8 }} sx={{ height: '100%', overflow: 'hidden' }}>
            <Slide direction="left" in={true} mountOnEnter unmountOnExit>
              <Box id="rules-detail" sx={{ height: '100%', minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                {panelMode === 'view' && selectedRuleId ? (
                  // Show detail panel if we have a selected rule ID, even if data is refetching
                  // This keeps the panel open during refetch after edit
                  selectedRule ? (
                    <RuleDetailPanel
                      fillHeight={false}
                      rule={selectedRule}
                      onClose={handlePanelClose}
                      onEdit={handleEditRule}
                      onDelete={handleDeleteRule}
                    />
                  ) : (
                    // Show loading state while rule is refetching
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )
                ) : panelMode === 'create' || panelMode === 'edit' ? (
                  <RuleFormPanel
                    fillHeight={false}
                    mode={panelMode}
                    rule={selectedRule ?? null}
                    onClose={handlePanelClose}
                    onSuccess={handleFormSuccess}
                  />
                ) : null}
              </Box>
            </Slide>
          </Grid>
        )}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <DeleteRuleDialog
        open={deleteDialogOpen}
        rule={ruleToDelete}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </Container>
  );
};
